import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { customResetPassword, login } from "../../services/api";
import imageAside from "../../images/form-aside.png";

const ResetPassword = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const expires = params.get("expires") || "";
    const origin = params.get("origin") || "";
    const userId = params.get("user_id") || "";
    const signature = params.get("signature") || "";

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!password || !confirmPassword) {
            setError("Please enter and confirm your new password.");
            return;
        }
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }

        setIsSubmitting(true);
        try {
            // Call backend to reset using signed params
            const payload = {
                expires,
                origin,
                user_id: userId,
                signature,
                password,
                password_confirmation: confirmPassword,
            };
            const response = await customResetPassword(payload);

            // If backend returns token and user directly (preferred)
            if (response?.data?.token && response?.data?.user) {
                sessionStorage.setItem("token", response.data.token);
                sessionStorage.setItem("user_id", response.data.user.id);
                sessionStorage.setItem("user_name", response.data.user.first_name);
                sessionStorage.setItem("user_email", response.data.user.email);
                sessionStorage.setItem("user", JSON.stringify(response.data.user));
                navigate("/");
                return;
            }

            // If token is not returned, try logging in if backend returns email
            const email = response?.data?.email;
            if (email) {
                const formData = new FormData();
                formData.append("email", email);
                formData.append("password", password);
                const loginResp = await login(formData);
                if (loginResp?.data?.token) {
                    sessionStorage.setItem("token", loginResp.data.token);
                    sessionStorage.setItem("user_id", loginResp.data.user.id);
                    sessionStorage.setItem("user_name", loginResp.data.user.first_name);
                    sessionStorage.setItem("user_email", loginResp.data.user.email);
                    sessionStorage.setItem("user", JSON.stringify(loginResp.data.user));
                    navigate("/");
                    return;
                }
            }

            // Fallback: navigate to login with a success note
            navigate("/", { state: { message: "Password has been reset. Please login." } });
        } catch (err) {
            const backendMsg = err?.response?.data?.message || err?.response?.data?.error;
            setError(backendMsg || "Unable to reset password. The link may be invalid or expired.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const linkInvalid = !expires || !signature || !userId;

    return (
		<div className="login-register">
			<div className="container d-flex align-items-center mvh-100">
			<Link className="back-btn mr-3 " to="/"><i className="fa fa-arrow-left" aria-hidden="true"></i></Link>
			<section className="login-registration rounded-3 overflow-hidden shadow bg-light">
				<div className="row align-items-stretch">
					<div className="col-md-6 bg-secondary-s text-center text-white">
						<div className="p-5">
							<h1 className="h3">Reset Your Password</h1>
							<p>
								Enter a new password for your account to continue to Florida Driving Permit.
								<br></br>
								{error && <span className="text-danger">{error}</span>}
							</p>
							<form className="text-dark py-4" onSubmit={handleSubmit}>
								<div className="row">
									<div className="col-lg-6">
										<div className="form-floating mt-4">
											<input
												type="password"
												className="form-control"
												id="password"
												name="password"
												placeholder=" "
												value={password}
												onChange={(e) => setPassword(e.target.value)}
												disabled={linkInvalid || isSubmitting}
											/>
											<label htmlFor="password">New Password</label>
										</div>
									</div>
									<div className="col-lg-6">
										<div className="form-floating mt-4">
											<input
												type="password"
												className="form-control"
												id="confirm_password"
												name="confirm_password"
												placeholder=" "
												value={confirmPassword}
												onChange={(e) => setConfirmPassword(e.target.value)}
												disabled={linkInvalid || isSubmitting}
											/>
											<label htmlFor="confirm_password">Confirm New Password</label>
										</div>
									</div>
									<div className="col-12">
										{linkInvalid && (
											<div className="alert alert-danger mt-4" role="alert">
												Invalid reset link. Please request a new email.
											</div>
										)}
										<button 
											type="submit" 
											className="btn btn-primary-s px-4 py-3 my-4 w-100 text-white"
											disabled={isSubmitting || linkInvalid}
										>
											{isSubmitting ? (
												<>
													<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
													Resetting...
												</>
											) : (
												'Reset Password'
											)}
										</button>
									</div>
								</div>
							</form>
						</div>
					</div>
					<div className="col-md-6 text-center">
						<div className="p-5">
							<Link to="/" className="mb-2 mt-4 wow animated fadeInLeft hide-mobile logoText">
								<h3>Florida <span>Driving</span> Permit</h3>
							</Link>
							<br /><br />
							<hr className="opacity-50" />
							<img src={imageAside} alt="Form Aside" className="img-fluid mt-5 w-85" />
						</div>
					</div>
				</div>
			</section>
			</div>
		</div>
	);
};

export default ResetPassword;


